<template>
    <div>
        <transition name="fade" mode="out-in">
            <div v-if="!paymentMethods || !paymentMethods.length">
                <v-row v-if="isShowTypeSwitcher" class="my-2">
                    <v-col cols="12" md="3">
                        <v-select
                            v-model="invoiceType"
                            :items="invoiceTypes"
                            hide-details
                            variant="outlined"
                            @update:model-value="onInvoiceTypeChange"
                        />
                    </v-col>
                </v-row>
                <v-expansion-panels v-model="panel">
                    <v-expansion-panel elevation="0">
                        <v-expansion-panel-title class="width-auto text-primary text-capitalize">
                            {{ $t('filters') }}
                        </v-expansion-panel-title>
                        <v-expansion-panel-text>
                            <v-row class="py-4">
                                <v-col md="8" cols="12" class="filters-form">
                                    <v-row>
                                        <v-col md="6" cols="12">
                                            <order-service-daterange
                                                :start-date-label="$t('due_date_from')"
                                                :end-date-label="$t('due_date_to')"
                                                :outlined="false"
                                                :single-line="false"
                                                :no-title="true"
                                                clearable
                                                dense
                                                :_start-date="filters.dueDateFrom"
                                                :_end-date="filters.dueDateTo"
                                                @change-start-date="filters = {...filters, dueDateFrom: $event}"
                                                @change-end-date="filters = {...filters, dueDateTo: $event}"
                                            />
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <v-text-field
                                                v-model="filters.orderId"
                                                :label="$t('order_number')"
                                                density="compact"
                                                hide-details
                                                variant="outlined"
                                            />
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <private-client-and-companies-autocomplete
                                                ref="customerAutocomplete"
                                                v-model="selectedClient"
                                                :show-icon="false"
                                                :label="$t('client')"
                                                :relationship="['CLIENT']"
                                                dense
                                                disable-persistent-placeholder
                                                :rules="[]"
                                                @change="onClientNameFilterChange"
                                            />
                                        </v-col>
                                        <v-col md="6" cols="12">
                                            <order-service-daterange
                                                :start-date-label="$t('pay_date_from')"
                                                :end-date-label="$t('pay_date_to')"
                                                :outlined="false"
                                                :single-line="false"
                                                :no-title="true"
                                                clearable
                                                dense
                                                :_start-date="filters.payDateFrom"
                                                :_end-date="filters.payDateTo"
                                                @change-start-date="filters = {...filters, payDateFrom: $event}"
                                                @change-end-date="filters = {...filters, payDateTo: $event}"
                                            />
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <v-select
                                                v-model="filters.paymentStatus"
                                                :items="paymentStatuses"
                                                :label="$t('status')"
                                                clearable
                                                :item-title="'text'"
                                                :item-value="'value'"
                                                density="compact"
                                                hide-details
                                                variant="outlined"
                                            />
                                        </v-col>
                                        <v-col cols="6" md="3">
                                            <supplier-select-autocomplete
                                                ref="supplierAutocomplete"
                                                v-model="selectedSupplier"
                                                :rules="[]"
                                                :label="$t('supplier_name')"
                                                dense
                                                disable-persistent-placeholder
                                                @change="onSupplierNameFilterChange"
                                            />
                                        </v-col>
                                    </v-row>
                                </v-col>
                                <v-spacer />
                                <v-col md="4" cols="12">
                                    <v-row>
                                        <v-col v-if="$conf.public.account.paymentsPageSaveSelections" cols="12" md="4">
                                            <v-btn block :color="'primary'" size="small" @click="saveSelections">
                                                {{ $t('save_selections') }}
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-btn
                                                block
                                                :color="isSomeFiltersSelected ? 'primary' : 'white'"
                                                size="small"
                                                @click="loadManualInvoices"
                                            >
                                                {{ $t('filter') }}
                                            </v-btn>
                                        </v-col>
                                        <v-col cols="12" md="4">
                                            <v-btn color="white" block size="small" @click="onResetClick">
                                                {{ $t('reset') }}
                                            </v-btn>
                                        </v-col>
                                    </v-row>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>
                <div class="d-flex align-center">
                    <span v-if="manualInvoicesCount" class="text-caption text--secondary ms-1">
                        {{ `${$t('invoices.invoices')}: ${manualInvoicesCount}` }}
                    </span>
                    <file-download-modal
                        v-model="isShowDownloadExcelMenu"
                        :label="$t('payments_download')"
                        :loading="isLoadingDownloadExcel"
                        :radio-items="radioItemsDownloadExcel"
                        @change-radio-group="radioGroupDownloadExcel = $event"
                        @download="downloadExcel"
                    />
                </div>
                <v-data-table-server
                    :items-length="manualInvoicesCount"
                    :items="manualInvoices"
                    :headers="headers"
                    :hide-default-footer="isManualInvoicesLoading"
                    :loading="isManualInvoicesLoading"
                    disable-sort
                    :footer-props="{'items-per-page-options': [10, 50, 100, -1]}"
                    :options="options"
                    class="elevation-1"
                    @update:options="onUpdateTableOptions"
                >
                    <template v-slot:header.dueDate="{column}">
                        <th role="columnheader" scope="col" aria-label="Amount" class="text-center">
                            <div class="d-flex align-center">
                                <v-checkbox-btn
                                    v-model="selectAll"
                                    :ripple="false"
                                    class="mt-n1"
                                    hide-details
                                    @update:model-value="changeSelectAll"
                                />
                                <span>{{ column.title }}</span>
                            </div>
                        </th>
                    </template>
                    <template v-slot:item.dueDate="{item}">
                        <div class="d-flex align-center">
                            <v-checkbox-btn
                                v-model="selectedInvoices"
                                :disabled="item.paymentStatus !== 'BILL'"
                                :ripple="false"
                                :value="item"
                                hide-details
                                class="mt-n1 flex-0-0"
                                @click.stop
                            />
                            <template v-if="item !== editDueDateItem">
                                <div v-if="item.dueDate" class="text-no-wrap">
                                    {{ $filters.dateShortFormat(item.dueDate) }}
                                </div>
                                <v-btn
                                    v-if="item.dueDate && isTO1"
                                    size="small"
                                    icon="mdi-pencil"
                                    color="primary"
                                    variant="text"
                                    @click="editDueDate(item)"
                                />
                            </template>
                            <template v-else>
                                <date-picker
                                    v-if="item === editDueDateItem"
                                    v-model="newDueDate"
                                    outlined
                                    dense
                                    hide-details
                                />
                                <v-btn
                                    :disabled="!newDueDate"
                                    size="small"
                                    icon="mdi-check-circle-outline"
                                    color="success"
                                    variant="text"
                                    @click="saveNewDueDate"
                                />
                            </template>
                        </div>
                    </template>
                    <template v-slot:item.paymentStatus="{item}">
                        <div class="d-flex flex-column">
                            <div class="d-flex align-center">
                                <span :class="getStatusColor(item)">
                                    {{ $t(`paymentStatus.${item.paymentStatus}`) }}
                                </span>
                                <v-tooltip v-if="isShowInfoButton(item)" location="top">
                                    <template v-slot:activator="{props}">
                                        <v-btn
                                            icon="mdi-information-outline"
                                            size="small"
                                            v-bind="item.paymentInfos.find(i => !!i.remark) ? props : null"
                                            variant="text"
                                            @click="
                                                showedBankTransferInvoiceId === item.invoiceId
                                                    ? (showedBankTransferInvoiceId = null)
                                                    : (showedBankTransferInvoiceId = item.invoiceId)
                                            "
                                        />
                                    </template>
                                    <div class="d-flex flex-column">
                                        <span v-for="info in item.paymentInfos" :key="info.remark">{{
                                            info.remark
                                        }}</span>
                                    </div>
                                </v-tooltip>
                            </div>
                            <v-expand-transition>
                                <div v-if="showedBankTransferInvoiceId === item.invoiceId" class="d-flex flex-column">
                                    <span v-if="item.description" class="text-caption">
                                        {{ `${$t('description')}: ${item.description}` }}
                                    </span>
                                    <div v-for="(info, i) in item.paymentInfos" :key="i" class="d-flex flex-column">
                                        <div
                                            v-if="Object.keys({...info.bankTransferDetails}).length"
                                            class="d-flex flex-column"
                                        >
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.account') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.account }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.bank') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.bank }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.branch') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.branch }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.iban') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.iban }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.receiving_bank') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.receivingBank }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.reference') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.reference }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.swift_code') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.bankTransferDetails.swiftCode }}
                                                </span>
                                            </div>
                                            <div>
                                                <span>
                                                    {{ $t('bank_transfer.value_date') + ': ' }}
                                                </span>
                                                <span v-if="info.bankTransferDetails.valueDate">
                                                    {{ $filters.dateShortFormat(info.bankTransferDetails.valueDate) }}
                                                </span>
                                            </div>
                                            <div v-if="info.remark">
                                                <span>
                                                    {{ $t('remark') + ': ' }}
                                                </span>
                                                <span>
                                                    {{ info.remark }}
                                                </span>
                                            </div>
                                        </div>
                                        <div
                                            v-if="info.exchangeRatesMap && Object.values(info.exchangeRatesMap).length"
                                        >
                                            <span>
                                                {{ $t('exchange_rate') + ': ' }}
                                            </span>
                                            <span>
                                                {{ Object.values(info.exchangeRatesMap)[0] }}
                                            </span>
                                        </div>
                                        <v-divider v-if="i !== item.paymentInfos.length - 1" class="my-1" />
                                    </div>
                                </div>
                            </v-expand-transition>
                        </div>
                    </template>
                    <template v-slot:item.payDate="{item}">
                        <div class="d-flex align-center">
                            <template v-if="item !== editPayDateItem">
                                <div v-if="item.payDate" class="text-no-wrap">
                                    {{ $filters.dateShortFormat(item.payDate) }}
                                </div>
                                <v-btn
                                    v-if="isTO1"
                                    size="small"
                                    icon="mdi-pencil"
                                    color="primary"
                                    variant="text"
                                    @click="editPayDate(item)"
                                />
                            </template>
                            <template v-else>
                                <date-picker
                                    v-if="item === editPayDateItem"
                                    v-model="newPayDate"
                                    outlined
                                    :min="minPayDate"
                                    dense
                                    hide-details
                                />
                                <v-btn
                                    :disabled="!newPayDate"
                                    :loading="newPayDateSaveLoading"
                                    size="small"
                                    icon="mdi-check-circle-outline"
                                    color="success"
                                    variant="text"
                                    @click="saveNewPayDate"
                                />
                            </template>
                        </div>
                    </template>
                    <template v-slot:item.billDate="{item}">
                        <div v-if="item.billDate" class="text-no-wrap">
                            {{ $filters.dateShortFormat(item.billDate) }}
                        </div>
                    </template>
                    <template v-slot:item.requestReference="{item}">
                        <nuxt-link
                            no-prefetch
                            :to="{name: 'order-details', params: {id: +item.requestReference}}"
                            @click.native.stop
                        >
                            <div
                                v-if="item.requestReference"
                                class="text-no-wrap font-weight-medium"
                                v-text="+item.requestReference"
                            />
                        </nuxt-link>
                    </template>
                    <template v-slot:item.amount="{item}">
                        <span v-if="item.originalPrices && item.originalPrices[0]">
                            {{
                                $filters.formatPrice({
                                    amount: item.originalPrices[0].originalAmount,
                                    currency: item.originalPrices[0].originalAmountCurrency,
                                })
                            }}
                        </span>
                    </template>
                    <template v-slot:item.amountToPay="{item}">
                        <span v-if="item.originalPrices && item.originalPrices[0]">
                            {{
                                $filters.formatPrice({
                                    amount: item.paymentStatus === 'BILL' ? item.originalPrices[0].originalAmount : 0,
                                    currency: item.originalPrices[0].originalAmountCurrency,
                                })
                            }}
                        </span>
                    </template>
                    <template v-slot:item.pay="{item}">
                        <v-btn
                            v-if="item.paymentStatus === 'BILL'"
                            color="primary"
                            size="small"
                            elevation="0"
                            :disabled="isLoading"
                            @click="initPay(item)"
                        >
                            <v-icon start size="small"> mdi-plus-circle-multiple-outline</v-icon>
                            <span class="text-caption">
                                {{ $t('pay') }}
                            </span>
                        </v-btn>
                    </template>
                    <template v-slot:item.cancel="{item}">
                        <v-btn
                            v-if="isShowCancelButton(item)"
                            size="small"
                            elevation="0"
                            :disabled="isLoading"
                            color="error"
                            @click="isShowCancelModalItem = item"
                        >
                            <span class="text-caption">
                                {{ $t('cancel') }}
                            </span>
                        </v-btn>
                    </template>
                </v-data-table-server>
                <v-row>
                    <v-col cols="12">
                        <v-btn
                            color="primary"
                            elevation="0"
                            class="me-2"
                            :disabled="isLoading || !selectedInvoices.length || isSelectedInvoicesDifferentCurrencies"
                            @click="initPay"
                        >
                            <v-icon start size="small"> mdi-plus-circle-multiple-outline </v-icon>
                            <span class="text-caption">
                                {{ $t('pay') }}
                            </span>
                        </v-btn>
                        <template v-if="isSelectedInvoicesDifferentCurrencies">
                            <span class="text-caption text-error">
                                {{ $t('invoices.select_same_currency') }}
                            </span>
                        </template>
                        <template v-else-if="totalPriceToPay.amount">
                            <span class="text-caption"> {{ $t('invoices.total_for_selected') }}: </span>
                            <span class="text-caption">
                                {{ $filters.priceFormat(totalPriceToPay.amount, totalPriceToPay.currency) }}
                            </span>
                        </template>
                    </v-col>
                </v-row>
            </div>
            <v-row v-else>
                <v-col cols="12">
                    <div class="d-flex align-center">
                        <v-btn class="me-3" size="small" elevation="0" @click="paymentMethods = []">
                            <v-icon> mdi-arrow-left</v-icon>
                        </v-btn>
                        <span class="text-h6">
                            {{ $t('payment') }}
                        </span>
                    </div>
                </v-col>
                <v-col cols="12">
                    <order-manual-invoice-payment-form
                        :payment-methods="paymentMethods"
                        :order-id="orderId"
                        :invoice-type="invoiceType"
                        :client-price-currencies="currencies"
                        multiple-currency-change
                        @complete-payment="completePayment"
                    />
                </v-col>
            </v-row>
        </transition>
        <confirmation-modal
            :model-value="!!isShowCancelModalItem"
            :text="$t('invoice_cancel_confirmation_label')"
            :confirmation-btn-text="$t('confirm')"
            :close-btn-text="$t('cancel')"
            @close="isShowCancelModalItem = null"
            @confirm="cancelInvoice(isShowCancelModalItem)"
        />
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Watch} from 'vue-facing-decorator'
    import OrderManualInvoicePaymentForm from '@/components/account/OrderManualInvoicePaymentForm.vue'
    import DatePicker from '@/components/snippets/forms/DatePicker.vue'
    import {downloadData, matches} from '@/utils/helpers'
    import {OrderPaymentSchedule} from '~src/components/account/OrderPaymentSchedule.vue'
    import {convertPrice} from '@/utils/filters'
    import OrderServiceDaterange from '@/components/account/filters/OrderServiceDaterange.vue'
    import SupplierSelectAutocomplete from '@/components/search/forms/SupplierSelectAutocomplete.vue'
    import PrivateClientAndCompaniesAutocomplete from '@/components/search/forms/PrivateClientAndCompaniesAutocomplete.vue'
    import FileDownloadModal from '~src/components/account/snippets/FileDownloadModal.vue'

    @Component({
        components: {
            PrivateClientAndCompaniesAutocomplete,
            SupplierSelectAutocomplete,
            OrderServiceDaterange,
            OrderManualInvoicePaymentForm,
            DatePicker,
            FileDownloadModal,
        },
    })
    export class PaymentsPageSchedule extends OrderPaymentSchedule {
        tableOptions = {
            itemsPerPage: 10,
            page: 1,
        }
        selectAll = false
        selectedInvoices = []
        manualInvoicesCount = 0
        paymentsPageSelection = null
        filters = {}
        panel = null

        selectedClient = null
        selectedSupplier = null

        isShowDownloadExcelMenu = false
        isLoadingDownloadExcel = false
        radioGroupDownloadExcel = ''

        client = null
        supplier = null

        @Watch('filters', {deep: true})
        onFiltersChange() {
            Object.keys(this.filters).forEach(key =>
                this.filters[key] === undefined || this.filters[key] === null || this.filters[key] === ''
                    ? delete this.filters[key]
                    : {}
            )
        }

        onSupplierNameFilterChange(val) {
            this.filters = {...this.filters, supplierName: val?.name}
        }

        onClientNameFilterChange(val) {
            this.filters = {
                ...this.filters,
                customerName: val ? this.$refs.customerAutocomplete.inputText(val, null, true) : undefined,
            }
        }

        onUpdateTableOptions(options) {
            this.options = options
        }

        get options() {
            return this.tableOptions
        }

        set options(options) {
            if (this.isLoading) return
            const prev = this.tableOptions
            if (!prev || matches(options, prev)) return
            this.tableOptions = options
            if (options.itemsPerPage === -1) {
                this.filters = {...this.filters, paymentStatus: 'BILL'}
            }
            this.loadManualInvoices()
        }

        @Emit()
        async completePayment() {
            this.reset()
            this.$toast.success(this.$t('invoices.marked_paid_success_message'))
            await this.loadManualInvoices()
        }

        async created() {
            if (this.$conf.public.account.paymentsPageSaveSelections) {
                this.panel = 0
            }
        }

        async mounted() {
            if (this.$conf.public.account.paymentsPageSaveSelections) {
                await this.loadPaymentsPageSelections()
            }

            await this.loadManualInvoices()
        }

        async onInvoiceTypeChange() {
            this.resetFilters()
            this.tableOptions = {
                itemsPerPage: 10,
                page: 1,
            }
            if (this.$conf.public.account.paymentsPageSaveSelections) {
                await this.loadPaymentsPageSelections()
            }
            this.loadManualInvoices()
        }

        async loadPaymentsPageSelections() {
            try {
                this.paymentsPageSelection = (
                    await this.$api.paymentsPageSelection.get({
                        companyId: authStore.person?.companyId,
                    })
                )?.[0]
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
                return
            }
            if (this.invoiceType === 'CUSTOMER' && this.paymentsPageSelection?.filtersCustomer) {
                this.filters = {...this.paymentsPageSelection.filtersCustomer}
            } else if (this.invoiceType === 'SUPPLIER' && this.paymentsPageSelection?.filtersSupplier) {
                this.filters = {...this.paymentsPageSelection.filtersSupplier}
            }

            if (this.invoiceType === 'CUSTOMER' && this.paymentsPageSelection?.itemsPerPageCustomer) {
                this.tableOptions = {
                    ...this.tableOptions,
                    itemsPerPage: this.paymentsPageSelection?.itemsPerPageCustomer,
                }
            } else if (this.invoiceType === 'SUPPLIER' && this.paymentsPageSelection?.itemsPerPageSupplier) {
                this.tableOptions = {
                    ...this.tableOptions,
                    itemsPerPage: this.paymentsPageSelection?.itemsPerPageSupplier,
                }
            }

            await this.$nextTick()

            if (this.filters.customerName) {
                await this.$refs.customerAutocomplete.onInput(this.filters.customerName)
                this.selectedClient = this.$refs.customerAutocomplete.clients?.[0]
            }

            if (this.filters.supplierName) {
                await this.$refs.supplierAutocomplete.onInput(this.filters.supplierName)
                this.selectedSupplier = this.$refs.supplierAutocomplete.clients?.[0]
            }
        }

        async onResetClick() {
            this.resetFilters()
            await this.loadManualInvoices()
        }

        @Emit()
        async loadManualInvoices() {
            this.manualInvoices = []
            this.selectAll = false
            this.changeSelectAll(false)

            this.isManualInvoicesLoading = true

            try {
                let objectId

                //TODO  temporary solution, backend edits required "objectId"
                if (this.filters.orderId) {
                    const order = (
                        await this.$api.orders.get({
                            orderType: authStore.orderType,
                            responseType: 'SHORT',
                            orderId: this.filters.orderId,
                        })
                    )?.orders?.[0]

                    objectId = order.objectId
                }

                const manualInvoicesResponse = await this.$api.manualInvoices.get({
                    companyId: authStore.person.companyId,
                    invoiceType: this.invoiceType,
                    limit: this.options.itemsPerPage,
                    offset: this.options.itemsPerPage === -1 ? 0 : this.options.itemsPerPage * (this.options.page - 1),
                    ...this.filters,
                    ...(objectId && {orderId: objectId}),
                })

                this.manualInvoicesCount = manualInvoicesResponse.invoicesCount

                let manualInvoices = manualInvoicesResponse.invoices
                    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
                    .sort(a => (a.paymentStatus === 'CANCELED' ? 1 : -1))
                    .sort(a => (a.paymentStatus === 'BILL' ? -1 : 1))

                if (this.cancelInvoicesOnLoad && !this.silentPay) {
                    await this.cancelInvoiceList(manualInvoices.filter(invoice => invoice.paymentStatus === 'BILL'))
                }

                manualInvoices = manualInvoices.filter(el =>
                    this.$conf.public.account.paymentSchedulerAutoPaymentMode ? el.paymentStatus !== 'CANCELED' : true
                )

                this.manualInvoices = manualInvoices

                await this.$nextTick()

                if (
                    this.invoiceType === 'CUSTOMER' &&
                    this.paymentsPageSelection?.selectedInvoicesIdsCustomer?.length
                ) {
                    this.paymentsPageSelection?.selectedInvoicesIdsCustomer.forEach(selectionId => {
                        const selectedInvoiceExists = this.selectedInvoices.find(s => s.invoiceId === selectionId)
                        const manualInvoiceExists = this.manualInvoices.find(m => m.invoiceId === selectionId)
                        if (!selectedInvoiceExists && manualInvoiceExists) {
                            this.selectedInvoices.push(manualInvoiceExists)
                        }
                    })
                }
                if (
                    this.invoiceType === 'SUPPLIER' &&
                    this.paymentsPageSelection?.selectedInvoicesIdsSupplier?.length
                ) {
                    this.paymentsPageSelection?.selectedInvoicesIdsSupplier.forEach(selectionId => {
                        const selectedInvoiceExists = this.selectedInvoices.find(s => s.invoiceId === selectionId)
                        const manualInvoiceExists = this.manualInvoices.find(m => m.invoiceId === selectionId)
                        if (!selectedInvoiceExists && manualInvoiceExists) {
                            this.selectedInvoices.push(manualInvoiceExists)
                        }
                    })
                }

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.isManualInvoicesLoading = false
            }

            await this.$nextTick(() => {})

            return this.manualInvoices
        }

        async saveSelections() {
            try {
                this.paymentsPageSelection = {
                    companyId: authStore.person?.companyId,
                    ...(this.invoiceType === 'CUSTOMER' && {filtersCustomer: {...this.filters}}),
                    ...(this.invoiceType === 'SUPPLIER' && {filtersSupplier: {...this.filters}}),
                    ...(this.invoiceType === 'CUSTOMER' && {
                        selectedInvoicesIdsCustomer: [...this.selectedInvoices].map(i => i.invoiceId),
                    }),
                    ...(this.invoiceType === 'SUPPLIER' && {
                        selectedInvoicesIdsSupplier: [...this.selectedInvoices].map(i => i.invoiceId),
                    }),
                    ...(this.invoiceType === 'CUSTOMER' && {itemsPerPageCustomer: this.options.itemsPerPage}),
                    ...(this.invoiceType === 'SUPPLIER' && {itemsPerPageSupplier: this.options.itemsPerPage}),
                }
                await this.$api.paymentsPageSelection.post(this.paymentsPageSelection)

                this.$toast.success(this.$t('success_message.saved_successfully'))
            } catch {
                this.$toast.error(this.$t('error_message.save_error'))
            }
        }

        async getPaymentOptions() {
            this.paymentOptionsLoading = true

            try {
                return (
                    await this.$api.manualInvoicePaymentMethods.get({
                        invoiceType: this.invoiceType,
                        invoiceId: this.selectedInvoices.map(i => i.invoiceId),
                    })
                ).invoicePaymentMethods

                // eslint-disable-next-line no-empty
            } catch (e) {
            } finally {
                this.paymentOptionsLoading = false
            }
        }

        async initPay() {
            this.paymentMethods = await this.getPaymentOptions()

            return this.paymentMethods
        }

        changeSelectAll(selectAll) {
            if (selectAll) {
                this.selectedInvoices = this.manualInvoices.filter(el => el.paymentStatus === 'BILL')
            } else {
                this.selectedInvoices = []
            }
        }

        resetFilters() {
            this.filters = {}
            this.selectedClient = null
            this.selectedSupplier = null
        }

        async downloadExcel(format) {
            this.isLoadingDownloadExcel = true
            let objectId

            //TODO  temporary solution, backend edits required "objectId"
            if (this.filters.orderId) {
                try {
                    const order = (
                        await this.$api.orders.get({
                            orderType: authStore.orderType,
                            responseType: 'SHORT',
                            orderId: this.filters.orderId,
                        })
                    )?.orders?.[0]

                    objectId = order.objectId
                } catch (e) {
                    /* empty */
                }
            }

            const rq =
                this.radioGroupDownloadExcel === 'filtered'
                    ? {
                          companyId: authStore.person?.companyId,
                          invoiceType: this.invoiceType,
                          fileType: format.fileType,
                          ...this.filters,
                          ...(objectId && {orderId: objectId}),
                      }
                    : {
                          companyId: authStore.person?.companyId,
                          invoiceType: this.invoiceType,
                          fileType: format.fileType,
                      }
            let data
            try {
                data = await this.$api.downloadManualInvoices.get(rq)
            } catch {
                this.$toast.error(this.$t('error_message.load_error'))
                this.isLoadingDownloadExcel = false
                return
            }
            await downloadData(data, 'manualInvoices.xlsx')
            this.isShowDownloadExcelMenu = false
            this.radioGroupDownloadExcel = ''
        }

        get isSomeFiltersSelected() {
            return Object.keys(this.filters).length !== 0
        }

        get headers() {
            const headers = [
                {title: this.$t('invoices.due_date'), value: 'dueDate'},
                {title: this.$t('invoices.amount'), value: 'amount', align: 'center'},
                {title: this.$t('invoices.amount_to_pay'), value: 'amountToPay', align: 'center'},
                {title: this.$t('status'), value: 'paymentStatus'},
                {title: this.$t('order_number'), value: 'requestReference'},
                {title: this.$t('client'), value: 'customerName'},
                {title: this.$t('supplier_name'), value: 'supplierName'},
                {title: this.$t('invoices.bill_date'), align: 'left', value: 'billDate'},
            ]

            if (this.manualInvoices.find(m => !!m.payDate)) {
                headers.push({title: this.$t('invoices.pay_date'), value: 'payDate'})
            }

            if (this.isTO1 && this.manualInvoices.find(m => m.dueDate && m.paymentStatus !== 'CANCELED')) {
                headers.push({title: this.$t('cancel'), value: 'cancel', align: 'center'})
            }

            return headers
        }

        get totalPriceToPay() {
            return this.selectedInvoices.reduce(
                (totalPrice, invoice) => {
                    const invoicePrice = {
                        amount: invoice.originalPrices[0].originalAmount,
                        currency: invoice.originalPrices[0].originalAmountCurrency,
                    }

                    if (!totalPrice.currency) {
                        totalPrice.currency = invoicePrice.currency
                    }

                    const priceConverted = convertPrice(invoicePrice, totalPrice.currency)
                    totalPrice.amount += priceConverted.amount

                    return totalPrice
                },
                {
                    amount: 0,
                    currency: null,
                }
            )
        }

        get paymentStatuses() {
            return [
                {
                    text: this.$t('paymentStatus.BILL'),
                    value: 'BILL',
                },
                {
                    text: this.$t('paymentStatus.PAID'),
                    value: 'PAID',
                },
                {
                    text: this.$t('paymentStatus.CANCELED'),
                    value: 'CANCELED',
                },
            ]
        }

        get isSelectedInvoicesDifferentCurrencies() {
            if (!this.selectedInvoices.length) {
                return false
            }

            const currency = this.selectedInvoices[0]?.originalPrices[0].originalAmountCurrency

            return !this.selectedInvoices.every(
                invoice => invoice?.originalPrices[0]?.originalAmountCurrency === currency
            )
        }

        get currencies() {
            return Object.values(this.$conf.public.currency.available).filter(currency =>
                Object.keys(runtimeStore.exchangeRates).includes(currency)
            )
        }

        get radioItemsDownloadExcel() {
            return [
                {
                    value: 'filtered',
                    label: `${this.$t('filtered_payments')} (${
                        this.isSomeFiltersSelected ? this.manualInvoicesCount : 0
                    })`,
                    disabled: !this.manualInvoices?.length,
                },
                {value: 'all', label: this.$t('all_payments')},
            ]
        }
    }

    export default PaymentsPageSchedule
</script>

<style scoped lang="scss">
    :deep(.filters-form) {
        .v-expansion-panel-header {
            min-height: auto;
            padding: 0;
            margin: 0 0 10px;
            font-size: 12px;
            color: rgba(0, 0, 0, 0.6);
        }
        .v-expansion-panel-content__wrap {
            padding: 0;
        }

        .v-text-field .v-label,
        .v-radio .v-label,
        .v-input--switch .v-label,
        .v-text-field input,
        .v-select__selections {
            font-size: 12px;
        }
    }

    .fade-enter-active,
    .fade-leave-active {
        transition: opacity 0.5s;
    }
    .fade-enter,
    .fade-leave-to {
        opacity: 0;
    }

    :deep(table) > tbody > tr > td:nth-child(1),
    :deep(table) > thead > tr > th:nth-child(1) {
        position: sticky !important;
        left: 0;
        background: white;
    }
</style>
